// === NOTES DE MISE À JOUR PUBLIQUES ===
// Affichées dans PatchNotesScene + popup auto au premier lancement après update.
// Synthèse joueur-friendly du CHANGELOG : pas de jargon technique, pas de chemins
// de fichiers, pas de noms de sprint. Une entrée = un changement perceptible.
// À mettre à jour à chaque release notable (les fixes mineurs peuvent être groupés).

export type PatchEntryKind = 'new' | 'fix' | 'improvement';

export interface PatchEntry {
  kind: PatchEntryKind;
  text: string;
}

export interface PatchVersion {
  version: string;
  date: string;          // YYYY-MM-DD
  /** Une-line headline optionnelle, mise en avant en gras sous le titre */
  headline?: string;
  entries: PatchEntry[];
}

export const PATCH_KIND_INFO: Record<PatchEntryKind, { label: string; emoji: string; color: string }> = {
  new:         { label: 'Nouveau',     emoji: '🆕', color: '#2ecc71' },
  improvement: { label: 'Amélioration', emoji: '✨', color: '#3498db' },
  fix:         { label: 'Correction',  emoji: '🛠️', color: '#f39c12' },
};

/** Versions affichées (les plus récentes en premier) */
export const PATCH_NOTES: PatchVersion[] = [
  {
    version: '0.40.7-beta',
    date: '2026-05-20',
    headline: 'Petit polish : étoiles de niveau visibles sur tes survivants.',
    entries: [
      { kind: 'improvement', text: 'Les survivants de niveau 2 et plus affichent maintenant leurs étoiles de progression (★ à ★★★★★) en doré au-dessus de leur tête, sur l\'île. Cachées quand tu cliques pour lire leur dialogue.' },
      { kind: 'improvement', text: 'Le mode Dev (F1) gagne un bouton "⬆️ Lvl up tous les survivants" pour tester rapidement les étoiles.' },
    ],
  },
  {
    version: '0.40.6-beta',
    date: '2026-05-20',
    headline: 'L\'île pixel art devient le camp par défaut. L\'ancien camp reste accessible.',
    entries: [
      { kind: 'improvement', text: 'L\'île pixel art est désormais activée par défaut pour tous les joueurs — plus besoin de l\'activer manuellement via F1. C\'est la nouvelle expérience standard du Camp.' },
      { kind: 'improvement', text: 'L\'ancien camp reste accessible via le panneau d\'affichage (« 📜 Ancien camp ») au cas où tu en aurais besoin. Un nouveau bouton « 🏝️ Île → » apparaît dans l\'en-tête de l\'ancien camp pour revenir à l\'île facilement.' },
      { kind: 'improvement', text: 'Le menu Dev (F1) garde le toggle pour forcer l\'ancien camp en mode debug.' },
    ],
  },
  {
    version: '0.40.5-beta',
    date: '2026-05-20',
    headline: 'L\'île respire : vagues animées, mouettes, feu crépitant, cycle jour/nuit.',
    entries: [
      { kind: 'new', text: 'Les vagues de l\'océan défilent doucement autour de l\'île (toggle de frames toutes les ~1,1 s, décalé une tuile sur deux pour casser le synchrone).' },
      { kind: 'new', text: 'Trois mouettes survolent l\'île en cercles lents — battement d\'ailes, flip selon le sens, trajectoires distinctes (rayon et période différents par mouette).' },
      { kind: 'new', text: 'Le feu de camp crépite : fumée grise qui monte et étincelles orange/jaune qui jaillissent (particules Phaser).' },
      { kind: 'new', text: 'Cycle jour/nuit léger basé sur l\'heure réelle : teinte bleu nuit après 22 h, lever orangé à 6 h, journée neutre, coucher orange à 18 h. Subtile respiration d\'alpha (±15 % sur 60 s).' },
    ],
  },
  {
    version: '0.40.4-beta',
    date: '2026-05-20',
    headline: 'Popup "Quoi de neuf" : récap multi-versions si tu reviens après une longue pause.',
    entries: [
      { kind: 'improvement', text: 'La popup au lancement après mise à jour affiche désormais TOUTES les versions ratées depuis ton dernier passage (jusqu\'à 8), pas seulement la plus récente — pratique si tu as 5 ou 10 versions de retard.' },
      { kind: 'improvement', text: 'La version la plus récente est mise en avant (⭐ étoile + couleur dorée), avec un aperçu des 3 changements phares en bas de la popup.' },
      { kind: 'improvement', text: 'Si tu as plus de 8 versions de retard, un compteur "… et X autres versions" apparaît en bas de la liste.' },
    ],
  },
  {
    version: '0.40.3-beta',
    date: '2026-05-20',
    headline: 'L\'île se peuple : tes survivants déambulent et te parlent.',
    entries: [
      { kind: 'new', text: 'Tes survivants vivants apparaissent maintenant sur l\'île pixel art — chaque classe a son look : Guerrier (bandana rouge + sabre), Tireur (capuche verte + arc) et Médecin (bonnet à croix rouge + tablier).' },
      { kind: 'new', text: 'Ils patrouillent autour de zones d\'ancrage (taverne, dortoir, feu de camp, sentiers) à pas lents — chacun fait son petit tour de garde toutes les 3 à 7 secondes.' },
      { kind: 'new', text: 'Clique sur un survivant pour lire une réplique pirate — chaque survivant a sa propre ligne du jour, qui change à minuit. Reviens les voir régulièrement, ils ont des choses à dire.' },
      { kind: 'improvement', text: 'Le mode Dev (F1) gagne deux boutons pour tester rapidement : "👥 Recruter 3 survivants test" et "💀 Tuer tous les survivants".' },
    ],
  },
  {
    version: '0.40.2-beta',
    date: '2026-05-19',
    headline: 'L\'île prend vie : zone de départ raid, chantier naval (Acte 2), menus diégétiques.',
    entries: [
      { kind: 'new', text: 'Un portail en bois recouvert de lianes apparaît au sud-est de l\'île — clique dessus pour partir en raid (ton capitaine marche jusqu\'à l\'arche, puis l\'écran de préparation s\'ouvre).' },
      { kind: 'new', text: 'Chantier Naval (teaser Acte 2) — une cale sèche avec une coque de bateau en construction est plantée sur la plage ouest. Verrouillée pour l\'instant : clic → tooltip "🔒 Bientôt disponible (Acte 2)".' },
      { kind: 'new', text: 'Tableau d\'affichage au centre de l\'île — clic pour ouvrir un menu avec Classement en ligne, Patchnotes, Roadmap, Signaler un bug, Retour menu principal et Ancien camp.' },
      { kind: 'new', text: 'Table de navigation près de la plage — clic pour ouvrir les Paramètres. Feu de camp animé au centre — clic pour voir tes Statistiques.' },
      { kind: 'new', text: '15 décorations pixel art posées sur l\'île : palmiers en bordure de plage, tonneaux, caisses, filets de pêche, petits rochers — pour le décor uniquement.' },
      { kind: 'improvement', text: 'Les boutons "Menu" et "Ancien camp" du header de l\'île sont retirés — toute la navigation passe désormais par les objets diégétiques.' },
    ],
  },
  {
    version: '0.40.1-beta',
    date: '2026-05-19',
    headline: 'L\'île se peuple : bâtiments cliquables et tooltips.',
    entries: [
      { kind: 'new', text: 'Six bâtiments apparaissent sur l\'île pixel art — Atelier, Coffre, Dortoir, Vigie, Taverne, Boutique — chacun avec un sprite pixel art dédié et son label.' },
      { kind: 'new', text: 'Clique sur un bâtiment : ton capitaine marche jusqu\'à la porte, puis la scène s\'ouvre automatiquement à l\'arrivée.' },
      { kind: 'new', text: 'Les bâtiments non construits apparaissent en fondations grisées avec une pancarte — un tooltip "À construire dans l\'Atelier" s\'affiche au clic.' },
      { kind: 'improvement', text: 'Sentiers procéduraux qui relient tous les bâtiments au centre de l\'île pour un déplacement plus fluide.' },
      { kind: 'improvement', text: 'Les boutons "← Camp" des sous-écrans (Coffre, Atelier, Dortoir, Vigie, Taverne) ramènent maintenant à l\'île si elle est activée — plus de retour à l\'ancien menu par accident.' },
    ],
  },
  {
    version: '0.40.0-beta',
    date: '2026-05-19',
    headline: 'Refonte du Camp : première vue de l\'île pixel art (preview).',
    entries: [
      { kind: 'new', text: 'Première version de l\'île pixel art top-down — désactivée par défaut le temps de la finaliser. Active-la via F1 → ÎLE PIXEL ART → 🏝️ Activer l\'île, puis reviens au menu et clique « Entrer au Camp ».' },
      { kind: 'new', text: 'Le capitaine se déplace au point & click (clique sur le sol pour aller où tu veux). L\'eau et les rochers bloquent — le sable, l\'herbe et les sentiers sont marchables.' },
      { kind: 'improvement', text: 'L\'ancien Camp reste l\'expérience par défaut ; les bâtiments cliquables, le coffre et tout le reste seront branchés sur l\'île au prochain sprint.' },
    ],
  },
  {
    version: '0.39.3-beta',
    date: '2026-05-18',
    headline: 'Vigie, Taverne : deux bugs majeurs corrigés.',
    entries: [
      { kind: 'fix', text: 'Vigie — assigner un survivant à une expédition ne déclenchait plus d\'ajouts fantômes au clic suivant n\'importe où sur l\'écran.' },
      { kind: 'fix', text: 'Taverne — fin des tours qui se jouaient tout seuls et du sens des joueurs qui partait en cacahuète après plusieurs manches.' },
    ],
  },
  {
    version: '0.39.2-beta',
    date: '2026-05-11',
    headline: 'Classement en ligne — comparez vos exploits !',
    entries: [
      { kind: 'new', text: 'Nouveau bouton 🏆 Classement en ligne dans le menu : top 50 mondial, filtrable par acte (1/2/3) et par période (jour/semaine/mois/total).' },
      { kind: 'new', text: 'Soumission automatique de votre score à chaque victoire (extraction ou boss final) — un choix d\'opt-in vous est demandé au premier envoi.' },
      { kind: 'new', text: 'Podium coloré (or/argent/bronze) et surbrillance de votre meilleur score dans la liste.' },
      { kind: 'improvement', text: 'Le jeu reste 100% jouable hors-ligne : si le serveur de classement est indisponible, vous voyez juste un message d\'info à la place.' },
    ],
  },
  {
    version: '0.39.1-beta',
    date: '2026-05-11',
    headline: 'Le jeu vous parle : carnet de bord et notes de version.',
    entries: [
      { kind: 'new', text: 'Carnet de Bord du Capitaine accessible depuis le menu — vous voyez ce qui est livré et ce qui arrive ensuite.' },
      { kind: 'new', text: 'Écran "Notes de mise à jour" : retrouvez l\'historique des changements à tout moment.' },
      { kind: 'new', text: 'Popup automatique au premier lancement après une mise à jour, avec un lien direct vers les nouveautés.' },
    ],
  },
  {
    version: '0.39.0-beta',
    date: '2026-05-11',
    headline: 'Sauvegarde portable + temps de jeu cumulé.',
    entries: [
      { kind: 'new', text: 'Export / Import de votre sauvegarde sous forme de fichier JSON (Paramètres → 💾 Sauvegarde) — pratique pour sync PC/mobile manuellement.' },
      { kind: 'new', text: 'Section "Vie de Capitaine" dans les Statistiques : temps de jeu cumulé, total de ressources collectées, classe favorite.' },
    ],
  },
  {
    version: '0.38.22-beta',
    date: '2026-05-10',
    headline: 'Filet de sécurité, fixes mobiles et signalements enrichis.',
    entries: [
      { kind: 'new', text: 'Quand le jeu plante, un écran clair propose "Retour au menu / Signaler / Copier" au lieu de tout perdre.' },
      { kind: 'new', text: 'Bouton "📚 Revoir les tutoriels" dans le menu et sur la carte.' },
      { kind: 'new', text: 'Identifiant unique visible sur chaque rapport de bug (plus facile pour le suivi).' },
      { kind: 'fix', text: 'Un consommable utilisé restait perdu si on rechargeait pendant un combat — corrigé.' },
      { kind: 'fix', text: 'Mobile : la barre de consommables est désormais visible en combat.' },
      { kind: 'fix', text: 'Mobile : plus de superposition des PA/PM sur le bouton "Fin de tour".' },
      { kind: 'fix', text: 'Captures d\'écran des rapports de bug réparées (étaient noires).' },
    ],
  },
  {
    version: '0.38.21-beta',
    date: '2026-05-06',
    headline: 'Fixes critiques sur l\'écran de préparation de run.',
    entries: [
      { kind: 'fix', text: 'Plus de perte d\'items après un refresh navigateur pendant la prépa de run.' },
      { kind: 'fix', text: 'Plus de perte silencieuse d\'items quand le coffre est plein (annulation propre).' },
      { kind: 'improvement', text: 'Écran d\'équipe restructuré : équipe sélectionnée vs survivants disponibles.' },
    ],
  },
  {
    version: '0.38.20-beta',
    date: '2026-05-04',
    headline: 'Polish mobile sur 22 scènes (popups, layouts, lisibilité).',
    entries: [
      { kind: 'improvement', text: 'Adaptation mobile : popups recentrées et descriptions qui ne débordent plus.' },
      { kind: 'improvement', text: 'Logo plus discret en portrait pour libérer de l\'espace.' },
      { kind: 'improvement', text: 'Boutons d\'action plus accessibles sur petit écran.' },
    ],
  },
  {
    version: '0.38.19-beta',
    date: '2026-05-04',
    headline: 'Combat mobile : panel sous la grille, plus de chevauchements.',
    entries: [
      { kind: 'improvement', text: 'Combat en portrait : panel d\'infos compacté, frise des tours adaptée aux denses encounters.' },
      { kind: 'improvement', text: 'Boutons "Prendre / Retirer / Ajouter" en prépa : zone tactile élargie.' },
    ],
  },
  {
    version: '0.38.16-beta',
    date: '2026-05-01',
    headline: 'Système de retours/suggestions (bouton 💡 à côté du 🐛).',
    entries: [
      { kind: 'new', text: 'Bouton 💡 partout dans le jeu pour envoyer suggestions, demandes d\'amélioration, retours d\'équilibrage.' },
    ],
  },
  {
    version: '0.38.15-beta',
    date: '2026-05-01',
    headline: 'Presets d\'équipement : sauvez vos loadouts.',
    entries: [
      { kind: 'new', text: '3 slots de presets en prépa de run : sauvegardez et chargez un loadout en 1 clic.' },
    ],
  },
  {
    version: '0.38.14-beta',
    date: '2026-05-01',
    headline: 'Liar\'s Dice : variante "Proposition juste".',
    entries: [
      { kind: 'new', text: 'Nouveau coup au dé menteur : appel "PROPOSITION JUSTE" — succès = +1 dé, échec = -1 dé.' },
    ],
  },
  {
    version: '0.38.13-beta',
    date: '2026-05-01',
    headline: 'Plus de clics fantômes en switchant de personnage en équipement.',
    entries: [
      { kind: 'fix', text: 'Plus de clics au mauvais endroit après avoir changé d\'onglet personnage en prépa / équipement.' },
      { kind: 'fix', text: 'Popups d\'équipement bien fermées lorsqu\'on change d\'onglet.' },
    ],
  },
  {
    version: '0.38.12-beta',
    date: '2026-05-01',
    headline: 'Soins et dégâts hors combat correctement appliqués.',
    entries: [
      { kind: 'fix', text: 'Le soin "Repos" en fin de combat soigne désormais réellement votre équipe.' },
      { kind: 'fix', text: 'Les pièges et plantes médicinales en exploration impactent bien les HP au combat suivant.' },
    ],
  },
  {
    version: '0.38.11-beta',
    date: '2026-05-01',
    headline: 'Fix : élite "Résurrection" ne semble plus soigner sur le killing blow.',
    entries: [
      { kind: 'fix', text: 'Quand un élite ressuscite (capacité spéciale), l\'effet visuel est désormais clair (aura verte + nombre flottant).' },
    ],
  },
  {
    version: '0.38.10-beta',
    date: '2026-04-29',
    headline: 'Skills de classe affichés correctement en combat.',
    entries: [
      { kind: 'fix', text: 'Le skill spécifique à votre classe (Provocation, Soin Ciblé, Tir de Recul…) apparaît dès le 1er combat.' },
      { kind: 'fix', text: 'Le panneau "Skill actif" se met à jour immédiatement quand vous changez de skill.' },
      { kind: 'new', text: 'Raccourci clavier "4" pour activer le skill de classe en combat.' },
    ],
  },
];
